// packages/charts/src/conditionalFormat.ts
//
// The one conditional-formatting evaluator (ADR-0351). Every surface that
// paints a table cell (dashboard table, pivot, report table, the mailed
// report, the XLSX payload) asks this function, so one rule cannot colour a
// cell two ways on two surfaces.
//
// It decides WHAT a cell gets (a paint, a text token, bold), never the
// colour itself: `formatTokens.ts` turns a paint into CSS per theme.
//
// Contract:
//   - Pure and total: it never throws. Stored specs are member-authorable
//     and may be newer (a cached bundle) or older (a restored version) than
//     this code, so every rule is coerced and an unknown shape is skipped.
//   - First match wins per cell, in list order; a threshold rule beats a
//     scale on the same cell.
//   - Output is keyed by the row's STABLE key, never its index, so colours
//     follow the value through a sort or a page change.

import type { ColumnSentiment } from "./metricSentiment";
import { isColorToken, type CfPaint, type ColorToken } from "./formatTokens";

export const CF_OPS = [
  "gt", "gte", "lt", "lte", "eq", "neq", "between", "not_between",
  "contains", "not_contains", "is_empty", "not_empty",
] as const;
export type CfOp = (typeof CF_OPS)[number];

const NUMERIC_OPS: ReadonlySet<string> = new Set(["gt", "gte", "lt", "lte", "between", "not_between"]);
const OP_SET: ReadonlySet<string> = new Set(CF_OPS);

export const MAX_CF_RULES = 20;
export const MAX_CF_STOPS = 5;

export type CfStopAt = "min" | "max" | "median" | number | { pct: number };

export type CfScale =
  | { mode: "sentiment" }
  | { mode: "stops"; stops: { at: CfStopAt; color: ColorToken }[] };

/** One stored rule, as the backend validator admits it
 *  (api/schemas/dashboard_schemas.py, ConditionalFormatRule). */
export type ConditionalFormatRule = {
  id: string;
  columns: string[];
  apply_to: "cell" | "text" | "row";
  kind: "rule" | "scale";
  when?: { op: CfOp; value?: number | string | null; value2?: number | string | null } | null;
  style?: { color?: ColorToken | null; bold?: boolean | null } | null;
  scale?: CfScale | null;
  totals?: "include" | "exclude" | "only" | null;
};

/** One row as the evaluator sees it. */
export type CfRow = {
  key: string;
  values: Record<string, unknown>;
  /** A total or subtotal row. */
  total?: boolean;
  /** A breakdown child row: threshold rules only, never a scale (the scale
   *  is fitted to the parents). */
  child?: boolean;
};

export type CfCellStyle = {
  fill?: CfPaint;
  text?: ColorToken;
  bold?: boolean;
  /** The rule that fired; undefined for the implicit heatmap rule. */
  ruleId?: string;
};

export type CfRowStyle = {
  rowFill?: CfPaint;
  rowBold?: boolean;
  rowRuleId?: string;
  cells: Map<string, CfCellStyle>;
};

export type CfResult = {
  rows: Map<string, CfRowStyle>;
  /** Rules none of whose columns are in the delivered result: the editor
   *  shows them as inactive instead of letting them fail silently. */
  inactiveRuleIds: string[];
};

export type CfInput = {
  rules: unknown;
  /** Legacy `heatmap_keys`: read as a `sentiment` scale per key (ADR-0351 §2). */
  heatmapKeys?: readonly string[];
  rows: readonly CfRow[];
  /** The columns this surface delivers. */
  columns: readonly string[];
  sentimentOf: (column: string) => ColumnSentiment;
  /** Decimals a numeric column displays, so `eq` compares what the reader
   *  sees ("12.3" equals 12.3 even when the stored value is 12.2999). */
  decimalsOf?: (column: string) => number | undefined;
};

// ---------------------------------------------------------------------------
// Coercion: stored JSON -> a rule this code understands, or null (skipped)
// ---------------------------------------------------------------------------

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function coerceStopAt(v: unknown): CfStopAt | null {
  if (v === "min" || v === "max" || v === "median") return v;
  if (finite(v)) return v;
  if (isObj(v) && finite(v.pct) && v.pct >= 0 && v.pct <= 100) return { pct: v.pct };
  return null;
}

function coerceScale(v: unknown): CfScale | null {
  if (!isObj(v)) return null;
  if (v.mode === "sentiment") return { mode: "sentiment" };
  if (v.mode !== "stops" || !Array.isArray(v.stops)) return null;
  if (v.stops.length < 2 || v.stops.length > MAX_CF_STOPS) return null;
  const stops: { at: CfStopAt; color: ColorToken }[] = [];
  for (const s of v.stops) {
    if (!isObj(s)) return null;
    const at = coerceStopAt(s.at);
    if (at === null || !isColorToken(s.color)) return null;
    stops.push({ at, color: s.color });
  }
  return { mode: "stops", stops };
}

export function coerceRule(raw: unknown): ConditionalFormatRule | null {
  if (!isObj(raw)) return null;
  const { id, columns, apply_to, kind } = raw;
  if (typeof id !== "string" || !id) return null;
  if (!Array.isArray(columns) || columns.length === 0) return null;
  const cols = columns.filter((c): c is string => typeof c === "string" && c.length > 0);
  if (cols.length !== columns.length) return null;
  if (apply_to !== "cell" && apply_to !== "text" && apply_to !== "row") return null;
  const totals = raw.totals == null ? "include" : raw.totals;
  if (totals !== "include" && totals !== "exclude" && totals !== "only") return null;

  if (kind === "scale") {
    // A scale paints cells; a row or text scale is not a shape we draw.
    if (apply_to !== "cell") return null;
    const scale = coerceScale(raw.scale);
    if (!scale) return null;
    return { id, columns: cols, apply_to, kind, scale, totals };
  }
  if (kind !== "rule") return null;

  const when = raw.when;
  if (!isObj(when) || typeof when.op !== "string" || !OP_SET.has(when.op)) return null;
  const op = when.op as CfOp;
  const value = when.value;
  const value2 = when.value2;
  if (NUMERIC_OPS.has(op) && !finite(value)) return null;
  if ((op === "between" || op === "not_between") && !finite(value2)) return null;
  if ((op === "eq" || op === "neq") && !finite(value) && typeof value !== "string") return null;
  if ((op === "contains" || op === "not_contains") && (typeof value !== "string" || value === "")) return null;
  // A row rule reads ONE driver column (ADR-0351 §1).
  if (apply_to === "row" && cols.length !== 1) return null;

  const style = isObj(raw.style) ? raw.style : {};
  const color = isColorToken(style.color) ? style.color : undefined;
  const bold = style.bold === true;
  if (!color && !bold) return null; // a rule that paints nothing
  return {
    id,
    columns: cols,
    apply_to,
    kind,
    when: { op, value: value as number | string | null, value2: (value2 ?? null) as number | string | null },
    style: { color: color ?? null, bold },
    totals,
  };
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

function toNumber(v: unknown): number | null {
  if (finite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const isEmpty = (v: unknown) => v === null || v === undefined || (typeof v === "string" && v.trim() === "");

type Matcher = (v: unknown, column: string) => boolean;

function matcherFor(rule: ConditionalFormatRule, decimalsOf?: (c: string) => number | undefined): Matcher {
  const { op, value, value2 } = rule.when!;
  switch (op) {
    case "is_empty":
      return (v) => isEmpty(v);
    case "not_empty":
      return (v) => !isEmpty(v);
    case "contains":
    case "not_contains": {
      // Lowercased once per rule, not once per cell.
      const needle = String(value).toLowerCase();
      const want = op === "contains";
      return (v) => !isEmpty(v) && String(v).toLowerCase().includes(needle) === want;
    }
    case "eq":
    case "neq": {
      const want = op === "eq";
      if (typeof value === "string") {
        const s = value.toLowerCase();
        return (v) => !isEmpty(v) && (String(v).toLowerCase() === s) === want;
      }
      const target = value as number;
      return (v, column) => {
        const n = toNumber(v);
        if (n === null) return false;
        const d = decimalsOf?.(column);
        const tol = d === undefined ? 1e-9 * Math.max(1, Math.abs(target)) : 0.5 * 10 ** -d;
        return (Math.abs(n - target) <= tol) === want;
      };
    }
    default: {
      const a = value as number;
      const lo = op === "between" || op === "not_between" ? Math.min(a, value2 as number) : a;
      const hi = op === "between" || op === "not_between" ? Math.max(a, value2 as number) : a;
      const test: (n: number) => boolean =
        op === "gt" ? (n) => n > a
        : op === "gte" ? (n) => n >= a
        : op === "lt" ? (n) => n < a
        : op === "lte" ? (n) => n <= a
        : op === "between" ? (n) => n >= lo && n <= hi
        : (n) => n < lo || n > hi;
      return (v) => {
        const n = toNumber(v);
        return n !== null && test(n);
      };
    }
  }
}

function totalsAllow(rule: ConditionalFormatRule, row: CfRow): boolean {
  const t = rule.totals ?? "include";
  if (t === "only") return row.total === true;
  if (t === "exclude") return row.total !== true;
  return true;
}

// ---------------------------------------------------------------------------
// Scales
// ---------------------------------------------------------------------------

function quantile(sorted: readonly number[], t: number): number {
  const idx = (sorted.length - 1) * t;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/** The detail values of a column: no totals, no child rows (ADR-0351 §1). */
function domainOf(rows: readonly CfRow[], column: string): number[] {
  const out: number[] = [];
  for (const r of rows) {
    if (r.total || r.child) continue;
    const n = toNumber(r.values[column]);
    if (n !== null) out.push(n);
  }
  return out.sort((a, b) => a - b);
}

type ScaleFn = (v: unknown) => CfPaint | undefined;

/** Today's heat, unchanged: median midpoint, p10/p90-fitted halves, the side
 *  flipped for an inverted metric, nothing for a neutral one. */
function sentimentScale(sorted: number[], sentiment: ColumnSentiment): ScaleFn | null {
  if (sentiment === "neutral" || sorted.length < 3) return null;
  const median = quantile(sorted, 0.5);
  const lowSpan = median - quantile(sorted, 0.1) || median - sorted[0];
  const highSpan = quantile(sorted, 0.9) - median || sorted[sorted.length - 1] - median;
  if (lowSpan <= 0 && highSpan <= 0) return null;
  return (v) => {
    const n = toNumber(v);
    if (n === null) return undefined;
    const span = n >= median ? highSpan : lowSpan;
    if (span <= 0) return undefined;
    let side = Math.max(-1, Math.min(1, (n - median) / span));
    if (sentiment === "invert") side = -side;
    return { kind: "sentiment", strength: side };
  };
}

function stopsScale(sorted: number[], stops: { at: CfStopAt; color: ColorToken }[]): ScaleFn | null {
  if (sorted.length === 0) return null;
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const pos = (at: CfStopAt): number =>
    at === "min" ? min
    : at === "max" ? max
    : at === "median" ? quantile(sorted, 0.5)
    : typeof at === "number" ? at
    : quantile(sorted, at.pct / 100);
  const resolved = stops.map((s) => ({ x: pos(s.at), color: s.color })).sort((a, b) => a.x - b.x);
  return (v) => {
    const n = toNumber(v);
    if (n === null) return undefined;
    const first = resolved[0];
    const last = resolved[resolved.length - 1];
    if (n <= first.x) return { kind: "token", token: first.color };
    if (n >= last.x) return { kind: "token", token: last.color };
    for (let i = 1; i < resolved.length; i += 1) {
      const a = resolved[i - 1];
      const b = resolved[i];
      if (n <= b.x) {
        const span = b.x - a.x;
        const t = span > 0 ? (n - a.x) / span : 1;
        return { kind: "blend", from: a.color, to: b.color, t };
      }
    }
    return { kind: "token", token: last.color };
  };
}

// ---------------------------------------------------------------------------
// The evaluator
// ---------------------------------------------------------------------------

type Compiled =
  | { kind: "rule"; rule: ConditionalFormatRule; match: Matcher }
  | { kind: "scale"; rule: ConditionalFormatRule | null; columns: string[]; scales: Map<string, ScaleFn> };

/** The rules a spec carries, coerced; at most MAX_CF_RULES, unknown shapes
 *  dropped. Exposed for the editor (inactive badge) and the export. */
export function readRules(raw: unknown): ConditionalFormatRule[] {
  if (!Array.isArray(raw)) return [];
  const out: ConditionalFormatRule[] = [];
  const seen = new Set<string>();
  for (const item of raw.slice(0, MAX_CF_RULES)) {
    const rule = coerceRule(item);
    if (!rule || seen.has(rule.id)) continue;
    seen.add(rule.id);
    out.push(rule);
  }
  return out;
}

export function evaluateConditionalFormats(input: CfInput): CfResult {
  const present = new Set(input.columns);
  const rules = readRules(input.rules);
  const inactiveRuleIds = rules.filter((r) => !r.columns.some((c) => present.has(c))).map((r) => r.id);

  const compiled: Compiled[] = [];
  const scaleFor = (rule: ConditionalFormatRule | null, scale: CfScale, columns: string[]) => {
    const scales = new Map<string, ScaleFn>();
    for (const col of columns) {
      if (!present.has(col)) continue;
      const sorted = domainOf(input.rows, col);
      const fn =
        scale.mode === "sentiment"
          ? sentimentScale(sorted, input.sentimentOf(col))
          : stopsScale(sorted, scale.stops);
      if (fn) scales.set(col, fn);
    }
    if (scales.size) compiled.push({ kind: "scale", rule, columns, scales });
  };

  for (const rule of rules) {
    if (!rule.columns.some((c) => present.has(c))) continue;
    if (rule.kind === "rule") compiled.push({ kind: "rule", rule, match: matcherFor(rule, input.decimalsOf) });
    else if (rule.scale) scaleFor(rule, rule.scale, rule.columns);
  }
  // The legacy heatmap is the LAST scale: an author's own rule on the same
  // column comes first.
  const heatKeys = (input.heatmapKeys ?? []).filter((k): k is string => typeof k === "string");
  if (heatKeys.length) scaleFor(null, { mode: "sentiment" }, heatKeys);

  const thresholds = compiled.filter((c): c is Extract<Compiled, { kind: "rule" }> => c.kind === "rule");
  const scales = compiled.filter((c): c is Extract<Compiled, { kind: "scale" }> => c.kind === "scale");
  const rowRules = thresholds.filter((c) => c.rule.apply_to === "row");
  const cellRules = thresholds.filter((c) => c.rule.apply_to !== "row");

  const out = new Map<string, CfRowStyle>();
  for (const row of input.rows) {
    const style: CfRowStyle = { cells: new Map() };

    for (const c of rowRules) {
      if (!totalsAllow(c.rule, row)) continue;
      const driver = c.rule.columns[0];
      if (!present.has(driver) || !c.match(row.values[driver], driver)) continue;
      const color = c.rule.style?.color;
      if (color) style.rowFill = { kind: "token", token: color };
      if (c.rule.style?.bold) style.rowBold = true;
      style.rowRuleId = c.rule.id;
      break; // first match wins
    }

    for (const col of input.columns) {
      const v = row.values[col];
      let cell: CfCellStyle | undefined;
      for (const c of cellRules) {
        if (!c.rule.columns.includes(col) || !totalsAllow(c.rule, row) || !c.match(v, col)) continue;
        const color = c.rule.style?.color ?? undefined;
        cell = {
          ...(c.rule.apply_to === "cell" && color ? { fill: { kind: "token", token: color } as CfPaint } : {}),
          ...(c.rule.apply_to === "text" && color ? { text: color } : {}),
          ...(c.rule.style?.bold ? { bold: true } : {}),
          ruleId: c.rule.id,
        };
        break;
      }
      if (!cell && !row.total && !row.child) {
        for (const s of scales) {
          const fn = s.scales.get(col);
          const paint = fn?.(v);
          if (!paint) continue;
          cell = { fill: paint, ...(s.rule ? { ruleId: s.rule.id } : {}) };
          break;
        }
      }
      if (cell) style.cells.set(col, cell);
    }

    if (style.rowFill || style.rowBold || style.cells.size) out.set(row.key, style);
  }
  return { rows: out, inactiveRuleIds };
}
